import { type Component, createSignal, onCleanup, onMount, Show } from 'solid-js'
import Button from '~/components/Button'
import Icon from '~/components/Icon'

interface SignaturePadPropsType {
  label?: string
  description?: string
  required?: boolean
  error?: string
  disabled?: boolean
  /** Data URL de la firma, o cadena vacía si aún no hay nada. */
  value?: string
  onChange?: (dataUrl: string) => void
}

/**
 * Panel para firmar con el dedo o el ratón. Devuelve un PNG en data URL, que es
 * lo que guarda la respuesta del formulario.
 *
 * Usa eventos de puntero (no de ratón) para que funcione igual en móvil, y
 * dibuja sobre un canvas escalado al devicePixelRatio para que el trazo no
 * salga borroso en pantallas densas.
 */
const SignaturePad: Component<SignaturePadPropsType> = props => {
  let canvas: HTMLCanvasElement | undefined
  let context: CanvasRenderingContext2D | null = null

  const [hasDrawing, setHasDrawing] = createSignal(false)
  let drawing = false

  const setupCanvas = () => {
    if (!canvas) return
    const ratio = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    if (rect.width === 0) return

    canvas.width = rect.width * ratio
    canvas.height = rect.height * ratio

    context = canvas.getContext('2d')
    if (!context) return
    context.scale(ratio, ratio)
    context.lineWidth = 2
    context.lineCap = 'round'
    context.lineJoin = 'round'
    // currentColor no existe en canvas: se lee el color de texto ya resuelto.
    context.strokeStyle = getComputedStyle(canvas).color || '#111827'
  }

  const pointFrom = (event: PointerEvent) => {
    const rect = canvas!.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  const start = (event: PointerEvent) => {
    if (props.disabled || !context || !canvas) return
    drawing = true
    canvas.setPointerCapture(event.pointerId)
    const { x, y } = pointFrom(event)
    context.beginPath()
    context.moveTo(x, y)
  }

  const move = (event: PointerEvent) => {
    if (!drawing || !context) return
    event.preventDefault()
    const { x, y } = pointFrom(event)
    context.lineTo(x, y)
    context.stroke()
  }

  const end = () => {
    if (!drawing) return
    drawing = false
    setHasDrawing(true)
    props.onChange?.(canvas?.toDataURL('image/png') ?? '')
  }

  const clear = () => {
    if (!canvas || !context) return
    context.clearRect(0, 0, canvas.width, canvas.height)
    setHasDrawing(false)
    props.onChange?.('')
  }

  onMount(() => {
    setupCanvas()
    // Redimensionar reinicia el canvas: se vuelve a preparar y se avisa que quedó vacío.
    const onResize = () => {
      setupCanvas()
      if (hasDrawing()) clear()
    }
    window.addEventListener('resize', onResize)
    onCleanup(() => window.removeEventListener('resize', onResize))
  })

  return (
    <div>
      <Show when={props.label}>
        <p class="text-sm font-medium text-foreground mb-1">
          {props.label}
          <Show when={props.required}>
            <span class="text-danger"> *</span>
          </Show>
        </p>
      </Show>
      <Show when={props.description}>
        <p class="text-xs text-foreground-subtle mb-2">{props.description}</p>
      </Show>

      <div class={`relative rounded-xl border bg-surface ${props.error ? 'border-danger' : 'border-border'}`}>
        <canvas ref={canvas} class="block h-40 w-full touch-none rounded-xl text-foreground" onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end} />

        <Show when={!hasDrawing()}>
          <div class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-foreground-subtle">
            <Icon icon="material-symbols-light:draw-outline" size={28} />
            <p class="text-xs mt-1">Firma aquí con el dedo o el ratón</p>
          </div>
        </Show>

        <div class="absolute bottom-2 right-2">
          <Button type="button" size="sm" variant="text" color="default" onClick={clear} disabled={props.disabled || !hasDrawing()}>
            Borrar
          </Button>
        </div>
      </div>

      <Show when={props.error}>
        <p class="text-xs text-danger mt-1">{props.error}</p>
      </Show>
    </div>
  )
}

export default SignaturePad
